import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from './i18n';
import { authFetch } from './auth';
import { matchKey } from './codes';
import { AXES } from './RadarChart';
import OfficialBadge from './OfficialBadge';
import { FoodSensation, FoodPhase, FOOD_PHASES, FOOD_MARK_FIELDS, FOOD_QUALIFIERS } from './foodSheetBlocks';
import DishAutocomplete from './DishAutocomplete';
import AttachmentPicker from './AttachmentPicker';
import { uploadAttachments } from './attachments';
import { ComparisonTable, FOOD_COMPARISON_SPEC } from './WineCatalog';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8100/api' : '/api';
const DRAFT_KEY = 'tnh2_draft_food';
const FOOD_AXES = AXES.filter((axis) => axis.side === 'food');

const emptyForm = () => ({
  dish_name: '', notes: '', visibility: 'private', venue: '', chef: '',
  tasting_date: new Date().toISOString().slice(0, 10),
  // the 12 Mercadini sensations start NOT FILLED IN (null, distinct from 0): the slider starts empty
  ...Object.fromEntries(FOOD_AXES.map((axis) => [axis.key, null])),
  // complete dish sheet (AIS/Mercadini): optional marks 1-10 (null = not scored), qualifiers and preparation
  ...Object.fromEntries(FOOD_MARK_FIELDS.map((field) => [field, null])),
  ...Object.fromEntries(Object.keys(FOOD_QUALIFIERS).map((field) => [field, ''])),
  preparation: '',
});

// Keeps only the known fields of an API object / stored draft, over the defaults
function pickKnown(source) {
  const form = emptyForm();
  Object.keys(form).forEach((key) => { if (source[key] !== undefined && source[key] !== null) form[key] = source[key]; });
  return form;
}

function loadDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return pickKnown(JSON.parse(saved));
  } catch { /* ignore */ }
  return emptyForm();
}

const emptyToNull = (value) => (typeof value === 'string' && value.trim() === '' ? null : value);

// Food sheet: sensory profile of a dish (Mercadini method), reusable in many pairings.
// Without initialData it creates a new sheet (POST /food-sheets); with initialData it edits that sheet (PUT).
// Props: initialData, onDone(sheet) (alias: onSubmit) called after a successful save, onCancel.
function FoodSheetForm({ initialData, onDone, onSubmit, onCancel }) {
  const { t } = useI18n();
  const editing = !!initialData;
  const [form, setForm] = useState(() => (initialData ? pickKnown(initialData) : loadDraft()));
  const [others, setOthers] = useState([]);
  const [attachmentItems, setAttachmentItems] = useState([]);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleChange = useCallback((name, value) => setForm((prev) => ({ ...prev, [name]: value })), []);

  // Draft of a new sheet
  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  // Other food sheets of the same dish, for the live comparison
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/food-sheets?limit=300`);
        if (res && res.ok && !abort) setOthers(await res.json());
      } catch { /* ignore */ }
    })();
    return () => { abort = true; };
  }, []);

  const key = matchKey(form.dish_name);
  const sameDish = key
    ? others.filter((sheet) => matchKey(sheet.dish_name) === key && (!editing || sheet.id !== initialData.id))
    : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.dish_name.trim()) { setMessage({ type: 'err', text: t('food.need_dish') }); return; }
    setSaving(true);
    const body = {
      ...form,
      venue: emptyToNull(form.venue),
      chef: emptyToNull(form.chef),
      notes: emptyToNull(form.notes),
      tasting_date: emptyToNull(form.tasting_date),
      preparation: emptyToNull(form.preparation),
      // marks are numbers or null, qualifiers a code or null; the means are computed by the server and never sent
      ...Object.fromEntries(FOOD_MARK_FIELDS.map((field) => [field, form[field] === '' || form[field] === undefined ? null : form[field]])),
      ...Object.fromEntries(Object.keys(FOOD_QUALIFIERS).map((field) => [field, emptyToNull(form[field])])),
    };
    try {
      const res = await authFetch(`${API_URL}/food-sheets${editing ? `/${initialData.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      if (!res || !res.ok) throw new Error();
      const sheet = await res.json();
      let uploadFailed = false;
      if (!editing && attachmentItems.length > 0) {
        const { failed } = await uploadAttachments(sheet.id, attachmentItems);
        uploadFailed = failed.length > 0;
      }
      if (!editing) {
        setForm(emptyForm());
        setAttachmentItems([]);
        localStorage.removeItem(DRAFT_KEY);
      }
      setMessage(uploadFailed
        ? { type: 'err', text: t('attachments.error.upload_failed') }
        : { type: 'ok', text: t(editing ? 'food.updated' : 'food.saved') });
      const done = onDone || onSubmit;
      if (done) done(sheet);
    } catch {
      setMessage({ type: 'err', text: t(editing ? 'food.error_update' : 'food.error') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="sheet-form" onSubmit={submit}>
      <h2 style={{ marginTop: 0 }}>{t(editing ? 'food.title_edit' : 'food.title_new')}</h2>
      <OfficialBadge status="unofficial" source={t('food.badge_source')} note={t('food.badge_note')} />
      <fieldset>
        <legend>{t('food.legend_dish')}</legend>
        <div className="form-field">
          <label>{t('food.dish')} *</label>
          <DishAutocomplete value={form.dish_name}
            onChange={(v) => handleChange('dish_name', v)}
            onSelect={(dish) => handleChange('dish_name', dish.name)} required />
        </div>
        <div className="form-grid">
          <div className="form-field">
            <label>{t('food.chef')}</label>
            <input value={form.chef || ''} onChange={(e) => handleChange('chef', e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t('food.venue')}</label>
            <input value={form.venue || ''} onChange={(e) => handleChange('venue', e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t('food.date')}</label>
            <input type="date" value={form.tasting_date || ''} onChange={(e) => handleChange('tasting_date', e.target.value)} />
          </div>
        </div>
        <div className="form-field">
          <label>{t('food.preparation')}</label>
          <textarea rows={2} maxLength={500} value={form.preparation || ''} placeholder={t('food.preparation_placeholder')}
            onChange={(e) => handleChange('preparation', e.target.value)} />
        </div>
        <div className="form-field">
          <label>{t('food.notes')}</label>
          <textarea rows={2} value={form.notes || ''} placeholder={t('food.notes_placeholder')}
            onChange={(e) => handleChange('notes', e.target.value)} />
        </div>
      </fieldset>

      <small className="import-hint">{t('food.phases_hint')}</small>
      {FOOD_PHASES.filter(({ phase }) => phase !== 'final').map(({ phase, fields }) => (
        <FoodPhase key={phase} phase={phase} fields={fields} form={form} onChange={handleChange} />
      ))}

      <fieldset>
        <legend className="legend-food">{t('food.legend_params')}</legend>
        <h4 className="section-subtitle">{t('pairing.form.sub_soft')}</h4>
        {FOOD_AXES.filter((_, i) => i < 5).map((axis) => (
          <FoodSensation key={axis.key} axisKey={axis.key} form={form} onChange={handleChange} />
        ))}
        <h4 className="section-subtitle">{t('pairing.form.sub_hard')}</h4>
        {FOOD_AXES.filter((_, i) => i >= 5 && i < 8).map((axis) => (
          <FoodSensation key={axis.key} axisKey={axis.key} form={form} onChange={handleChange} />
        ))}
        <h4 className="section-subtitle">{t('pairing.form.sub_aroma')}</h4>
        {FOOD_AXES.filter((_, i) => i >= 8).map((axis) => (
          <FoodSensation key={axis.key} axisKey={axis.key} form={form} onChange={handleChange} />
        ))}
      </fieldset>

      {FOOD_PHASES.filter(({ phase }) => phase === 'final').map(({ phase, fields }) => (
        <FoodPhase key={phase} phase={phase} fields={fields} form={form} onChange={handleChange} />
      ))}

      {sameDish.length > 0 && (
        <ComparisonTable ns="pairing" spec={FOOD_COMPARISON_SPEC}
          summary={t('food.compare_summary', { n: sameDish.length })}
          rows={[...sameDish, { ...form, id: 'current', columnLabel: t('food.current_col'), excludeFromStats: true }]} />
      )}

      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachmentItems} onChange={setAttachmentItems} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => handleChange('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      {message && <div className={`message ${message.type === 'err' ? 'error' : 'success'}`}>{message.text}</div>}

      <div className="form-buttons">
        <button type="submit" className="submit-btn" disabled={saving}>{t(editing ? 'food.btn_save_edit' : 'food.btn_save')}</button>
        {editing && <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>}
      </div>
    </form>
  );
}

export default FoodSheetForm;
